import { Page } from '@4beefopag/4bee-fopag-core/dist/organisms';
import { AuthContext, useHttpRequest } from '@4beefopag/4bee-fopag-core/dist/services/hooks';
import { DownloadOutlined } from '@ant-design/icons';
import { Dropdown, Modal, Spin, Tag } from 'antd';
import fileDownload from 'js-file-download';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import mostrarErro from '../../../services/util/error-handler';
import TENANT from '../../../constants/tenant';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../services/analytics';
import { APONTAMENTO } from '../../../services/apontamentoProducao';
import { createMetric } from '../../../services/repository/metricRepository';
import { buscarRelatorioOperacoes } from '../../../services/repository/operacaoLogRepository';
import {
  cancel,
  downloadApdataReportById,
  findForDetail,
  getPaymentStatementReport,
  refreshInvoiceById,
  transmitToApdata,
  downloadSegmentoZReceipt,
} from '../../../services/repository/payrollRepository';
import { downloadFile, getPublicUrlsByRequisicao } from '../../../services/upload';
import BwgButton from '../../atoms/Button';
import ButtonPanel from '../../molecules/ButtonPanel';
import ModalBaixarFaturaList from '../../molecules/ModalBaixarFaturaList';
import PayrollStatus from '../../molecules/PayrollStatus';
import PayrollDetail from '../../organisms/PayrollDetail';
import PayrollLogTable from '../../organisms/PayrollLogTable';
import PaymentStatementModal from '../../organisms/PaymentStatementModal';
import ModalBankAccounts from './ModalBankAccounts';
import DrawerCooperators from './DrawerCooperators';
import { handleDownloadWithMetrics } from '../../../services/util/file';
import { TRANSMITTABLE_PAYROLL_STATUS_IDS } from '../../../services/util/globalConstant';
import { Subjects, useUserPolicy } from '../../../user-policy';

// @TODO: Criar um componente?
const ButtonsColumn = styled.td`
  text-align: center !important;

  > button {
    padding-left: 0;
    padding-right: 0;
  }
`;

const getItemsFromPayroll = (payroll) => {
  if (!payroll) {
    return null;
  }
  return Array.isArray(payroll.items) && payroll.items.length > 0 ? payroll.items : null;
};

export default function PayrollDetails() {
  const { isAdmin } = useContext(AuthContext);
  const { userAccount } = useUserPolicy();
  const { id: payrollIdParam } = useParams();
  const [orderBy, setOrderBy] = useState({});
  const [logs, setLogs] = useState([]);
  const [isDownloadInvoicesModalVisible, setIsDownloadInvoicesModalVisible] = useState(false);
  const [isBankAccountsModalVisible, setIsBankAccountsModalVisible] = useState(false);
  const [isPaymentStatementModalVisible, setIsPaymentStatementModalVisible] = useState(false);
  const [paymentStatementData, setPaymentStatementData] = useState(null);
  const [loadingPaymentStatement, setLoadingPaymentStatement] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDrawerCooperatorsOpen, setIsDrawerCooperatorsOpen] = useState(false);
  const isSegmentoZReceiptDownloadEnabled = useFeatureFlag(FEATURE_FLAGS.DOWNLOAD_COMPROVANTE_SEGMENTO_Z);

  const {
    state: { data: payroll, loading: loadingLogs, error },
    request,
  } = useHttpRequest(() => findForDetail(payrollIdParam), {}, []);
  const {
    state: { data: invoicesFromNextSi, loading: loadingInvoices },
  } = useHttpRequest(() => refreshInvoiceById(payrollIdParam), {}, []);
  const { id: payrollId, folha } = payroll;
  const { name: nomeFolha } = folha || {};
  const [loadingTransmit, setLoadingTransmit] = useState(false);
  const [loadingSegmentoZReceipt, setLoadingSegmentoZReceipt] = useState(false);

  const payrollItems = getItemsFromPayroll(payroll);
  const canTransmitPayroll =
    userAccount?.ability?.can('process', Subjects.PAYROLL_TRANSMISSION) &&
    TRANSMITTABLE_PAYROLL_STATUS_IDS.includes(payroll.status);

  useEffect(() => {
    const [orderConfig] = Object.keys(orderBy);
    const sortedLogs = payroll.logs ? [...payroll.logs] : [];
    if (sortedLogs && Array.isArray(sortedLogs) && orderConfig) {
      sortedLogs.sort((a, b) => (a[orderConfig] > b[orderConfig] ? 1 : -1));

      if (orderBy[orderConfig] === 'desc') {
        sortedLogs.reverse();
      }
    }
    setLogs(sortedLogs);
  }, [payroll.logs, orderBy]);

  useEffect(() => {
    if (!payroll.id && error) {
      navigate('/not-found');
    }
  }, [payroll, error]);

  const exportLogs = async () => {
    await handleDownloadWithMetrics(
      async () => {
        const relatorioOperacoes = await buscarRelatorioOperacoes({ idsRequisicao: payrollIdParam });
        fileDownload(relatorioOperacoes, `operacoes-requisicao-${payrollIdParam}.xls`);
        Modal.success({
          title: 'Sucesso',
          content: <p>Logs exportados com sucesso!</p>,
        });
      },
      {
        key: 'payroll.export-operation-logs',
        source: 'payroll.details.download-button',
        details: { payrollId: payrollIdParam, type: payroll.tipo },
      }
    );
  };

  const fetchPaymentStatementData = async () => {
    setLoadingPaymentStatement(true);
    let status;
    try {
      const data = await getPaymentStatementReport(payrollIdParam);
      setPaymentStatementData(data);
      setIsPaymentStatementModalVisible(true);
      status = 'success';
    } catch (err) {
      status = 'error';
      mostrarErro(err);
    } finally {
      setLoadingPaymentStatement(false);
      await createMetric({
        key: 'payroll.payment-statement-report',
        source: 'payroll.details.preview-button',
        details: { payrollId: payrollIdParam, status },
      });
    }
  };

  const downloadApontamento = async () => {
    await handleDownloadWithMetrics(
      async () => {
        const relatorioOp = await downloadApdataReportById(payrollId);
        fileDownload(relatorioOp, `${nomeFolha}-${payrollId}.xls`);
      },
      {
        key: 'payroll.download-worksheet',
        source: 'payroll.details.download-button',
        details: { payrollId: payrollIdParam, type: payroll.tipo },
      }
    );
  };

  const downloadAnexos = async () => {
    await handleDownloadWithMetrics(
      async () => {
        const { data: fileList } = await getPublicUrlsByRequisicao(payrollId);
        fileList.forEach(({ nome, link }) => downloadFile(nome, link));
      },
      {
        key: 'payroll.download-attachments',
        source: 'payroll.details.download-button',
        details: { payrollId: payrollIdParam, type: payroll.tipo },
      }
    );
  };

  const downloadComprovanteSegmentoZ = async () => {
    setLoadingSegmentoZReceipt(true);
    try {
      const blob = await downloadSegmentoZReceipt(payrollId);
      fileDownload(blob, `comprovante-pagamento-requisicao-${payrollId}.pdf`);
      await createMetric({
        key: 'payroll.download-segmentoz-receipt',
        source: 'payroll.details.download-button',
        details: { payrollId: payrollIdParam, status: 'success' },
      });
    } catch (err) {
      mostrarErro(err);
      await createMetric({
        key: 'payroll.download-segmentoz-receipt',
        source: 'payroll.details.download-button',
        details: { payrollId: payrollIdParam, status: 'error' },
      });
    } finally {
      setLoadingSegmentoZReceipt(false);
    }
  };

  const handleTransmitToApdata = async () => {
    try {
      setLoadingTransmit(true);
      await transmitToApdata(payroll.id);
      await request();
      return Modal.success({
        title: 'Sucesso na transmissão',
        content: 'Requisição transmitida para o Apdata com sucesso!',
      });
    } catch (e) {
      if (e.response && e.response.status >= 400 && e.response.status < 500) {
        return Modal.error({
          title: 'Erro na transmissão',
          content: e.response.data.message,
        });
      }
      return mostrarErro(e);
    } finally {
      setLoadingTransmit(false);
    }
  };

  const handleOnBackClick = () => {
    if (location.key !== 'default') {
      return navigate(-1);
    }
    return navigate('/payrolls');
  };

  const handleOnCancelClick = async () => {
    try {
      await cancel(payrollIdParam);
      Modal.success({
        title: 'Sucesso',
        content: <p>Requisição cancelada com sucesso!</p>,
      });
      await request();
    } catch (err) {
      mostrarErro(err);
    }
  };

  const menuItems = [
    {
      label: (
        <span>
          <DownloadOutlined /> &nbsp; Apontamento
        </span>
      ),
      disabled: !payroll.canDownloadWorksheet,
      onClick: downloadApontamento,
    },
    {
      label: (
        <span>
          <DownloadOutlined /> &nbsp; Anexos
        </span>
      ),
      disabled: !payroll?.files?.length,
      onClick: downloadAnexos,
    },
  ];

  if ([APONTAMENTO.status.faturada, APONTAMENTO.status.pago].includes(payroll.status)) {
    menuItems.push({
      label: (
        <span>
          <DownloadOutlined /> &nbsp;
          {loadingInvoices ? (
            <>
              Carregando faturas, aguarde... &nbsp;
              <Spin size="small" />
            </>
          ) : (
            'Faturas'
          )}
        </span>
      ),
      disabled: !payroll.canDownloadInvoices || loadingInvoices,
      onClick: () => {
        if (TENANT.NOVACOOP === process.env.REACT_APP_COOP_NAME) {
          return setIsBankAccountsModalVisible(true);
        }

        return setIsDownloadInvoicesModalVisible(true);
      },
    });
  }

  if (payroll.status === APONTAMENTO.status.pago && isSegmentoZReceiptDownloadEnabled) {
    menuItems.push({
      label: (
        <span>
          <DownloadOutlined /> &nbsp;
          {loadingSegmentoZReceipt ? (
            <>
              Baixando comprovante... &nbsp;
              <Spin size="small" />
            </>
          ) : (
            'Comprovante de Pagamento'
          )}{' '}
          <Tag color="green">NOVO</Tag>
        </span>
      ),
      disabled: loadingSegmentoZReceipt,
      onClick: downloadComprovanteSegmentoZ,
    });
  }

  if (payroll.isPaymentStatementAvailable) {
    menuItems.push({
      label: (
        <span>
          <DownloadOutlined /> &nbsp; Visualizar Demonstrativo
        </span>
      ),
      disabled: loadingPaymentStatement,
      onClick: fetchPaymentStatementData,
    });
  }

  if (logs.length > 0) {
    menuItems.push({
      label: (
        <span>
          <DownloadOutlined /> &nbsp; Histórico de Atualizações
        </span>
      ),
      onClick: exportLogs,
    });
  }

  return (
    <Page
      projeto="Cooperativa"
      titulo="Detalhes da Requisição"
      linkManual={process.env.REACT_APP_LINK_MANUAL}
    >
      <DrawerCooperators
        isDrawerOpen={isDrawerCooperatorsOpen}
        onClose={() => setIsDrawerCooperatorsOpen(false)}
        items={payrollItems ?? []}
      />
      <PayrollStatus
        payroll={payroll}
        loading={loadingLogs}
      />
      <PayrollDetail
        payroll={payroll}
        loading={loadingLogs}
        items={payrollItems ?? []}
        onViewCooperatorsClick={() => setIsDrawerCooperatorsOpen(true)}
      />
      <PayrollLogTable
        logs={logs}
        onChangeOrder={setOrderBy}
        orderBy={orderBy}
      />
      <ButtonPanel>
        <BwgButton onClick={handleOnBackClick}>Voltar</BwgButton>
        {isAdmin() && payroll.canCancel && <BwgButton onClick={handleOnCancelClick}>Cancelar Requisição</BwgButton>}

        {canTransmitPayroll && (
          <BwgButton
            type="primary"
            disabled={loadingTransmit}
            onClick={handleTransmitToApdata}
          >
            Transmitir para Apdata
          </BwgButton>
        )}

        <ButtonsColumn onClick={(e) => e.stopPropagation()}>
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click', 'hover']}
          >
            <BwgButton>Fazer Download</BwgButton>
          </Dropdown>
        </ButtonsColumn>
      </ButtonPanel>
      <ModalBaixarFaturaList
        payrollId={payrollId}
        invoices={Array.isArray(invoicesFromNextSi) ? invoicesFromNextSi : []}
        open={isDownloadInvoicesModalVisible}
        onCancel={() => setIsDownloadInvoicesModalVisible(false)}
      />
      <ModalBankAccounts
        open={isBankAccountsModalVisible}
        onOkClick={() => {
          setIsBankAccountsModalVisible(false);
          setIsDownloadInvoicesModalVisible(true);
        }}
      />
      <PaymentStatementModal
        open={isPaymentStatementModalVisible}
        data={paymentStatementData}
        loading={loadingPaymentStatement}
        onClose={() => setIsPaymentStatementModalVisible(false)}
      />
    </Page>
  );
}
